import React from 'react';
import { Modal } from '../../../components/common/Modal';
import { useLanguage } from '../../../features/language/useLanguage';
import type { BulkPendingConfirm } from './useBulkWorkOrders';

interface Props {
  pending: BulkPendingConfirm | null;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}

/**
 * Scheduling a bulk batch is partial by design: complete cards move to
 * scheduled, incomplete ones stay drafts. This says so before anything is
 * written, since the write deducts stock.
 */
export const BulkScheduleConfirmModal: React.FC<Props> = ({
  pending,
  onClose,
  onConfirm,
  isSubmitting,
}) => {
  const { t } = useLanguage();
  if (!pending) return null;

  return (
    <Modal isOpen onClose={onClose} title={t('workOrders.bulk.confirmTitle')}>
      <div className="space-y-4" data-test-id="wo-bulk-schedule-confirm-modal">
        <p className="text-sm text-gray-700">
          {t('workOrders.bulk.confirmBody', {
            ready: pending.ready,
            pending: pending.pendingCodes.length,
          })}
        </p>
        <div className="rounded-md bg-gray-50 p-3">
          <p className="mb-1 text-xs font-medium text-gray-500">
            {t('workOrders.bulk.confirmPendingList')}
          </p>
          <ul className="space-y-0.5 text-sm text-gray-800">
            {pending.pendingCodes.map((code, index) => (
              <li key={code || `pending-${index}`}>{code || '-'}</li>
            ))}
          </ul>
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            {t('auth.common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting}
            className="rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            data-test-id="wo-bulk-schedule-confirm-button"
          >
            {isSubmitting ? t('workOrders.bulk.scheduling') : t('workOrders.bulk.confirmContinue')}
          </button>
        </div>
      </div>
    </Modal>
  );
};
